/**
 * Values the Report List can be filtered by — the measurement table of the report
 * template, grouped and ordered as printed. Keys are the template placeholders
 * (`{{LVIDd}}`), which is what the backend stores in report_measurement_values.
 *
 * The backend normalises units (velocity m/s, length cm, time ms). `unit` is what the
 * user types; `toStored` converts it to the backend's unit. Tissue Doppler velocities
 * (E', RV S') are read in cm/s, so they are entered in cm/s and divided by 100.
 */

export interface MeasurementFilterField {
  key: string;
  label: string;
  group: string;
  /** Unit the value is entered in ('' for a ratio). */
  unit: string;
  /** Multiply the entered value by this to get the stored unit. Default 1. */
  toStored?: number;
}

const CM_S = { unit: 'cm/s', toStored: 0.01 };

export const MEASUREMENT_FILTER_FIELDS: MeasurementFilterField[] = [
  // LV/RV
  { key: 'LVIDd', label: 'LVIDd', group: 'LV/RV', unit: 'cm' },
  { key: 'LVIDs', label: 'LVIDs', group: 'LV/RV', unit: 'cm' },
  { key: 'IVSWd', label: 'IVSWd', group: 'LV/RV', unit: 'cm' },
  { key: 'LVPWD', label: 'LVPWD', group: 'LV/RV', unit: 'cm' },
  { key: 'LVMI', label: 'LVMI', group: 'LV/RV', unit: 'g/m²' },
  { key: 'EF_Biplane', label: 'EF Biplane', group: 'LV/RV', unit: '%' },
  { key: 'RVS', label: "RVS'", group: 'LV/RV', ...CM_S },
  { key: 'TAPSE', label: 'TAPSE', group: 'LV/RV', unit: 'cm' },
  { key: 'RV_Base', label: 'RV Base', group: 'LV/RV', unit: 'cm' },
  { key: 'RVOT_VTI', label: 'RVOT VTI', group: 'LV/RV', unit: 'cm' },
  // Aortic Valve
  { key: 'LVOT_Vmax', label: 'LVOT Vmax', group: 'Aortic Valve', unit: 'm/s' },
  { key: 'LVOT_VTI', label: 'LVOT VTI', group: 'Aortic Valve', unit: 'cm' },
  { key: 'AV_Vmax', label: 'AV Vmax', group: 'Aortic Valve', unit: 'm/s' },
  { key: 'AV_PG', label: 'AV PG', group: 'Aortic Valve', unit: 'mmHg' },
  { key: 'AV_MG', label: 'AV MG', group: 'Aortic Valve', unit: 'mmHg' },
  { key: 'AV_VTI', label: 'AV VTI', group: 'Aortic Valve', unit: 'cm' },
  { key: 'AVA_VTI', label: 'AVA VTI', group: 'Aortic Valve', unit: 'cm²' },
  { key: 'AVA_Vmax', label: 'AVA Vmax', group: 'Aortic Valve', unit: 'cm²' },
  { key: 'AR_PHT', label: 'AR PHT', group: 'Aortic Valve', unit: 'ms' },
  { key: 'DPI', label: 'DPI', group: 'Aortic Valve', unit: '' },
  // Mitral Valve
  { key: 'MV_MG', label: 'MV MG', group: 'Mitral Valve', unit: 'mmHg' },
  { key: 'PHT', label: 'PHT', group: 'Mitral Valve', unit: 'ms' },
  { key: 'MVA_PHT', label: 'MVA (PHT)', group: 'Mitral Valve', unit: 'cm²' },
  // Diastology
  { key: 'E_vel', label: 'E vel', group: 'Diastology', unit: 'm/s' },
  { key: 'Decel_time', label: 'Decel time', group: 'Diastology', unit: 'ms' },
  { key: 'A_vel', label: 'A vel', group: 'Diastology', unit: 'm/s' },
  { key: 'EA', label: 'E/A', group: 'Diastology', unit: '' },
  { key: 'E_sept', label: "E' sept", group: 'Diastology', ...CM_S },
  { key: 'E_lateral', label: "E' lateral", group: 'Diastology', ...CM_S },
  { key: 'Avg_EE', label: "Avg E/E'", group: 'Diastology', unit: '' },
  { key: 'PVein_S', label: 'PVein S', group: 'Diastology', unit: 'm/s' },
  { key: 'PVein_D', label: 'PVein D', group: 'Diastology', unit: 'm/s' },
  // Chamber Size
  { key: 'LVEDVI', label: 'LVEDVI', group: 'Chamber Size', unit: 'mL/m²' },
  { key: 'LVESVI', label: 'LVESVI', group: 'Chamber Size', unit: 'mL/m²' },
  { key: 'LA_Area', label: 'LA Area', group: 'Chamber Size', unit: 'cm²' },
  { key: 'LA_Vol_Ind', label: 'LA Vol Ind', group: 'Chamber Size', unit: 'mL/m²' },
  { key: 'RA_Area', label: 'RA Area', group: 'Chamber Size', unit: 'cm²' },
  // Aorta
  { key: 'LVOT_Diam', label: 'LVOT Diam', group: 'Aorta', unit: 'cm' },
  { key: 'ARD', label: 'ARD', group: 'Aorta', unit: 'cm' },
  { key: 'ARD_Index', label: 'ARD Index', group: 'Aorta', unit: 'cm/m²' },
  { key: 'Aorta_STJ', label: 'Aorta STJ', group: 'Aorta', unit: 'cm' },
  { key: 'Asc_Aorta', label: 'Asc Aorta', group: 'Aorta', unit: 'cm' },
  { key: 'Asc_Aorta_Index', label: 'Asc Aorta Index', group: 'Aorta', unit: 'cm/m²' },
  { key: 'Arch', label: 'Arch', group: 'Aorta', unit: 'cm' },
  // Tricuspid Valve
  { key: 'TR_Vmax', label: 'TR Vmax', group: 'Tricuspid Valve', unit: 'm/s' },
  { key: 'TR_PG', label: 'TR PG', group: 'Tricuspid Valve', unit: 'mmHg' },
  { key: 'IVC', label: 'IVC', group: 'Tricuspid Valve', unit: 'cm' },
  { key: 'RAP', label: 'RAP', group: 'Tricuspid Valve', unit: 'mmHg' },
  { key: 'RVSP', label: 'RVSP', group: 'Tricuspid Valve', unit: 'mmHg' },
  // Pulmonary Valve
  { key: 'PV_Vmax', label: 'PV Vmax', group: 'Pulmonary Valve', unit: 'm/s' },
  { key: 'PV_PG', label: 'PV PG', group: 'Pulmonary Valve', unit: 'mmHg' },
  { key: 'QpQs', label: 'Qp:Qs', group: 'Pulmonary Valve', unit: '' },
];

export const MEASUREMENT_FILTER_FIELD_BY_KEY: Record<string, MeasurementFilterField> =
  Object.fromEntries(MEASUREMENT_FILTER_FIELDS.map(f => [f.key, f]));

/** Matches the backend limit (helper/reportMeasurementFilter.js). */
export const MAX_MEASUREMENT_CONDITIONS = 5;

/** Roles the backend lets filter by measurement. */
export const MEASUREMENT_FILTER_ROLES = [
  'Admin',
  'Cardiologist',
  'Radiologist',
  'Clinician',
  'Sonographer',
];

export type MeasurementOp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'between';

/** A condition as the backend takes it — values in the stored unit. */
export interface MeasurementCondition {
  key: string;
  op: MeasurementOp;
  value?: number;
  min?: number;
  max?: number;
}

const round = (n: number) => Math.round(n * 1e6) / 1e6;
const factorOf = (key: string) => MEASUREMENT_FILTER_FIELD_BY_KEY[key]?.toStored ?? 1;

/** Entered value → stored unit. */
export const toStoredValue = (key: string, entered: number) => round(entered * factorOf(key));
/** Stored unit → value as entered. */
export const toEnteredValue = (key: string, stored: number) => round(stored / factorOf(key));

/** The `measurementFilters` filter string (JSON) → conditions. Bad input → []. */
export function parseMeasurementConditions(raw?: string | null): MeasurementCondition[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter(c => c && MEASUREMENT_FILTER_FIELD_BY_KEY[c.key])
      : [];
  } catch {
    return [];
  }
}
